import { snapshotDebugAppState } from './debugAppStateRegistry'
import {
  deleteDebugBlob,
  deleteDebugBlobsForSession,
  putDebugBlob,
  type DebugBlobKind,
} from './debugStorage'

const STORAGE_KEY = 'timelining-debug-session-v2'
const LEGACY_STORAGE_KEY = 'timelining-debug-session-v1'
const MAX_EVENTS = 800
const MAX_NETWORK_EVENTS = 120
const MAX_TEXT_LENGTH = 90
const MAX_TOTAL_ATTACHMENT_BYTES = 15 * 1024 * 1024

export type DebugSessionEventType =
  | 'debug_started'
  | 'debug_stopped'
  | 'route'
  | 'click'
  | 'input'
  | 'submit'
  | 'focus'
  | 'visibility'
  | 'error'
  | 'keydown'
  | 'user_note'
  | 'network'
  | 'console'
  | 'screenshot'
  | 'attachment'
  | 'state_snapshot'
  | 'viewport'

export type DebugSessionEvent = {
  id: number
  type: DebugSessionEventType
  at: string
  path: string
  screen: string
  details: Record<string, unknown>
}

export type DebugScreenshotManifest = {
  id: string
  filename: string
  mimeType: string
  bytes: number
  width: number
  height: number
  capturedAt: string
  source: 'auto' | 'manual' | 'error'
  label?: string
}

export type DebugAttachmentManifest = {
  id: string
  filename: string
  mimeType: string
  bytes: number
  addedAt: string
  label?: string
}

export type DebugSession = {
  version: 2
  sessionId: string
  startedAt: string
  stoppedAt?: string
  userAgent: string
  viewport: string
  initialUrl: string
  events: DebugSessionEvent[]
  screenshots: DebugScreenshotManifest[]
  attachments: DebugAttachmentManifest[]
  networkCount: number
}

const nowIso = () => new Date().toISOString()

const randomId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

const createSession = (): DebugSession => ({
  version: 2,
  sessionId: `debug-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
  startedAt: nowIso(),
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
  viewport: typeof window !== 'undefined'
    ? `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio || 1}`
    : 'unknown',
  initialUrl: typeof window !== 'undefined' ? window.location.href : '',
  events: [],
  screenshots: [],
  attachments: [],
  networkCount: 0,
})

let memorySession: DebugSession | null = null
const listeners = new Set<() => void>()

const notifyListeners = () => {
  listeners.forEach((fn) => {
    try { fn() } catch { /* ignore */ }
  })
}

const readSession = (): DebugSession | null => {
  if (memorySession) return memorySession
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY)
      return null
    }
    const parsed = JSON.parse(raw) as DebugSession
    if (parsed?.version !== 2 || !Array.isArray(parsed.events)) return null
    parsed.screenshots ??= []
    parsed.attachments ??= []
    parsed.networkCount ??= 0
    memorySession = parsed
    return parsed
  } catch {
    return null
  }
}

const writeSession = (session: DebugSession) => {
  memorySession = session
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    // storage full — trim and retry once
    session.events = session.events.slice(-Math.floor(MAX_EVENTS / 2))
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    } catch {
      // give up; in-memory copy still authoritative for this tab
    }
  }
  notifyListeners()
}

const getPath = () => {
  if (typeof window === 'undefined') return ''
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

const cleanText = (value: string | null | undefined) =>
  (value ?? '').replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT_LENGTH)

const getVisibleHeadings = () => {
  if (typeof document === 'undefined') return []
  return Array.from(document.querySelectorAll('h1, h2, [aria-label]'))
    .filter((element) => {
      const rect = element.getBoundingClientRect()
      return rect.width > 0 && rect.height > 0 && rect.bottom >= 0 && rect.top <= window.innerHeight
    })
    .map((element) => cleanText(element.textContent || element.getAttribute('aria-label')))
    .filter(Boolean)
    .slice(0, 8)
}

export const getCurrentDebugScreen = () => {
  if (typeof document === 'undefined') return 'unknown'
  const title = cleanText(document.querySelector('h1')?.textContent)
  const section = cleanText(document.querySelector('[aria-label]')?.getAttribute('aria-label'))
  return title || section || document.title || 'unknown'
}

export const describeDebugElement = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return { target: 'unknown' }
  const interactive = target.closest('button, a, input, textarea, select, label, [role="button"], [role="tab"], [data-tour-id], [data-testid]') ?? target
  const rect = interactive.getBoundingClientRect()
  const input = interactive instanceof HTMLInputElement || interactive instanceof HTMLTextAreaElement || interactive instanceof HTMLSelectElement

  return {
    tag: interactive.tagName.toLowerCase(),
    id: interactive.id || undefined,
    role: interactive.getAttribute('role') || undefined,
    type: input ? interactive.type || interactive.tagName.toLowerCase() : undefined,
    name: input ? interactive.name || undefined : undefined,
    ariaLabel: interactive.getAttribute('aria-label') || undefined,
    title: interactive.getAttribute('title') || undefined,
    testId: interactive.getAttribute('data-testid') || undefined,
    tourId: interactive.getAttribute('data-tour-id') || undefined,
    text: input ? '[redacted input value]' : cleanText(interactive.textContent),
    checked: interactive instanceof HTMLInputElement && ['checkbox', 'radio'].includes(interactive.type)
      ? interactive.checked
      : undefined,
    rect: {
      x: Math.round(rect.x),
      y: Math.round(rect.y),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    },
  }
}

const pushEvent = (event: DebugSessionEvent, session: DebugSession) => {
  session.events = [...session.events, event].slice(-MAX_EVENTS)
}

// Blackout lets internal flows (launch → dashboard, end → support) run without
// polluting the recorded timeline. Anything appended while the depth is > 0 is
// dropped. Meta-events emitted by the debug UI itself (opening the popover,
// attaching files) also get skipped when their DOM target lives under
// [data-debug-ignore="true"] — the recorder consults it before calling us.
let debugRecorderBlackoutDepth = 0

export const withDebugRecorderBlackout = <T>(fn: () => T): T => {
  debugRecorderBlackoutDepth += 1
  try {
    return fn()
  } finally {
    debugRecorderBlackoutDepth = Math.max(0, debugRecorderBlackoutDepth - 1)
  }
}

export const isDebugRecorderBlackout = () => debugRecorderBlackoutDepth > 0

export const appendDebugSessionEvent = (
  type: DebugSessionEventType,
  details: Record<string, unknown> = {},
  options: { includeState?: boolean; force?: boolean } = {},
) => {
  if (debugRecorderBlackoutDepth > 0 && !options.force) return null
  const existing = readSession()
  const session = existing && !existing.stoppedAt ? existing : createSession()
  const event: DebugSessionEvent = {
    id: (session.events.at(-1)?.id ?? 0) + 1,
    type,
    at: nowIso(),
    path: getPath(),
    screen: getCurrentDebugScreen(),
    details: {
      visibleHeadings: getVisibleHeadings(),
      ...details,
    },
  }
  if (options.includeState) {
    event.details.appState = snapshotDebugAppState()
  }
  if (type === 'network') {
    session.networkCount += 1
    // keep network buffer bounded independently of the main ring
    const netEvents = session.events.filter((e) => e.type === 'network')
    if (netEvents.length >= MAX_NETWORK_EVENTS) {
      const toRemove = netEvents.slice(0, netEvents.length - MAX_NETWORK_EVENTS + 1)
      const removeIds = new Set(toRemove.map((e) => e.id))
      session.events = session.events.filter((e) => !removeIds.has(e.id))
    }
  }
  pushEvent(event, session)
  writeSession(session)
  return event
}

export const startDebugSession = () => {
  const current = readSession()
  if (current && !current.stoppedAt) return current
  const session = createSession()
  writeSession(session)
  // Force the bookend event through the blackout so the timeline always has
  // a "session opened here" marker even when launched inside a blackout.
  appendDebugSessionEvent('debug_started', { viewport: session.viewport }, { includeState: true, force: true })
  return readSession() ?? session
}

export const stopDebugSession = () => {
  const session = readSession()
  if (!session || session.stoppedAt) return session
  appendDebugSessionEvent('debug_stopped', {}, { includeState: true, force: true })
  const stopped = readSession()
  if (!stopped) return null
  stopped.stoppedAt = nowIso()
  writeSession(stopped)
  return stopped
}

export const getDebugSession = () => readSession()

export const getActiveSessionId = () => {
  const session = readSession()
  return session && !session.stoppedAt ? session.sessionId : undefined
}

export const clearDebugSession = () => {
  const existing = readSession()
  if (existing) {
    void deleteDebugBlobsForSession(existing.sessionId).catch(() => undefined)
  }
  memorySession = null
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(STORAGE_KEY)
  window.localStorage.removeItem(LEGACY_STORAGE_KEY)
  notifyListeners()
}

export const getDebugSessionIssueText = () => {
  const session = readSession()
  if (!session) return ''
  return JSON.stringify(session, null, 2)
}

export const subscribeDebugSession = (fn: () => void) => {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

const totalAttachmentBytes = (session: DebugSession) =>
  session.attachments.reduce((sum, a) => sum + a.bytes, 0)
    + session.screenshots.reduce((sum, s) => sum + s.bytes, 0)

export type AddScreenshotResult = {
  ok: true
  manifest: DebugScreenshotManifest
} | { ok: false; reason: string }

export const addDebugScreenshot = async (params: {
  blob: Blob
  width: number
  height: number
  capturedAt: string
  source: 'auto' | 'manual' | 'error'
  label?: string
  mimeType?: string
}): Promise<AddScreenshotResult> => {
  const session = readSession()
  if (!session || session.stoppedAt) return { ok: false, reason: 'no-session' }
  if (totalAttachmentBytes(session) + params.blob.size > MAX_TOTAL_ATTACHMENT_BYTES) {
    return { ok: false, reason: 'quota' }
  }
  const kind: DebugBlobKind = 'screenshot'
  const id = randomId('shot')
  const filename = `${id}.jpg`
  const mimeType = params.mimeType ?? 'image/jpeg'
  try {
    await putDebugBlob({
      id,
      sessionId: session.sessionId,
      kind,
      mimeType,
      size: params.blob.size,
      createdAt: params.capturedAt,
      filename,
      blob: params.blob,
    })
  } catch (error) {
    return { ok: false, reason: error instanceof Error ? error.message : 'storage-failed' }
  }
  const manifest: DebugScreenshotManifest = {
    id,
    filename,
    mimeType,
    bytes: params.blob.size,
    width: params.width,
    height: params.height,
    capturedAt: params.capturedAt,
    source: params.source,
    label: params.label?.trim() || undefined,
  }
  session.screenshots = [...session.screenshots, manifest]
  appendDebugSessionEvent('screenshot', {
    screenshotId: id,
    source: params.source,
    label: manifest.label,
    bytes: manifest.bytes,
  })
  return { ok: true, manifest }
}

export type AddAttachmentResult = {
  ok: true
  manifest: DebugAttachmentManifest
} | { ok: false; reason: string }

export const addDebugAttachment = async (params: {
  file: File
  label?: string
}): Promise<AddAttachmentResult> => {
  const session = readSession()
  if (!session || session.stoppedAt) return { ok: false, reason: 'no-session' }
  if (totalAttachmentBytes(session) + params.file.size > MAX_TOTAL_ATTACHMENT_BYTES) {
    return { ok: false, reason: 'quota' }
  }
  const id = randomId('att')
  const filename = params.file.name || `${id}.bin`
  const mimeType = params.file.type || 'application/octet-stream'
  try {
    await putDebugBlob({
      id,
      sessionId: session.sessionId,
      kind: 'attachment',
      mimeType,
      size: params.file.size,
      createdAt: nowIso(),
      filename,
      blob: params.file,
    })
  } catch (error) {
    return { ok: false, reason: error instanceof Error ? error.message : 'storage-failed' }
  }
  const manifest: DebugAttachmentManifest = {
    id,
    filename,
    mimeType,
    bytes: params.file.size,
    addedAt: nowIso(),
    label: params.label?.trim() || undefined,
  }
  session.attachments = [...session.attachments, manifest]
  appendDebugSessionEvent('attachment', {
    attachmentId: id,
    filename,
    mimeType,
    bytes: params.file.size,
    label: manifest.label,
  })
  return { ok: true, manifest }
}

export const removeDebugScreenshot = async (id: string) => {
  const session = readSession()
  if (!session) return
  session.screenshots = session.screenshots.filter((s) => s.id !== id)
  writeSession(session)
  await deleteDebugBlob(id).catch(() => undefined)
}

export const removeDebugAttachment = async (id: string) => {
  const session = readSession()
  if (!session) return
  session.attachments = session.attachments.filter((a) => a.id !== id)
  writeSession(session)
  await deleteDebugBlob(id).catch(() => undefined)
}

export const appendDebugUserNote = (note: string) => {
  const trimmed = note.trim()
  if (!trimmed) return null
  return appendDebugSessionEvent('user_note', { text: trimmed.slice(0, 4000) })
}

export const getDebugSessionTotals = () => {
  const session = readSession()
  if (!session) return { events: 0, screenshots: 0, attachments: 0, bytes: 0, network: 0 }
  return {
    events: session.events.length,
    screenshots: session.screenshots.length,
    attachments: session.attachments.length,
    bytes: totalAttachmentBytes(session),
    network: session.networkCount,
  }
}
